import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { usePatientPayments } from "features/consultations/hooks/usePatientPayments";
import { usePaymentsDue } from "features/consultations/hooks/usePaymentsDue";
import { useFamilyPayments } from "features/family/hooks/useFamilyPayments";

import { formatDate } from "../constants.jsx";

const usePaymentsData = (patientId) => {
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState("paymentsDue");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Payments Due defaults to hiding rows with nothing actually owed (0 or
  // missing amountDue) — a paid-off or waived charge otherwise clutters the
  // list of things a patient still needs to pay.
  const [showAllPaymentsDue, setShowAllPaymentsDue] = useState(false);

  const sharedQueryParams = {
    page: currentPage,
    rows: rowsPerPage,
    patientId,
    dueDateFrom: startDate || null,
    dueDateTo: endDate || null,
    enabled: !!patientId,
    refetchOnWindowFocus: false,
  };

  const paymentsDueData = usePaymentsDue({
    ...sharedQueryParams,
    enabled: activeTab === "paymentsDue" && !!patientId,
  });

  const patientPaymentsData = usePatientPayments({
    page: currentPage,
    rows: rowsPerPage,
    patientId,
    paymentDateFrom: startDate || null,
    paymentDateTo: endDate || null,
    enabled: activeTab === "patient" && !!patientId,
    refetchOnWindowFocus: false,
  });

  const familyPaymentsData = useFamilyPayments({
    ...sharedQueryParams,
    enabled: activeTab === "family" && !!patientId,
  });

  const activeData = {
    paymentsDue: paymentsDueData,
    patient: patientPaymentsData,
    family: familyPaymentsData,
  }[activeTab];

  const { data: paymentData, isLoading } = activeData;
  const totalRecords = paymentData?.totalRecords || 0;

  const filteredPayments = useMemo(() => {
    let payments = paymentData?.data || [];

    if (activeTab === "paymentsDue" && !showAllPaymentsDue) {
      payments = payments.filter((p) => parseFloat(p.amountDue) > 0);
    }

    if (!searchQuery) return payments;
    const q = searchQuery.toLowerCase();
    return payments.filter((p) => {
      const service = (
        p.practiceService?.name ||
        p.serviceName ||
        ""
      ).toLowerCase();
      const date = formatDate(
        p.consultation?.startTime ||
          p.consultation?.consultationDate ||
          p.startTime ||
          p.consultationDate ||
          p.dueDate ||
          p.paymentDate
      );
      return service.includes(q) || date.includes(q);
    });
  }, [paymentData, searchQuery, activeTab, showAllPaymentsDue]);

  const handleTabChange = (tab) => {
    setActiveTab(tab || "paymentsDue");
    setCurrentPage(1);
    setSelectedIds(new Set());
    queryClient.invalidateQueries({ queryKey: ["paymentsDue"] });
    queryClient.invalidateQueries({ queryKey: ["patientPayments"] });
    queryClient.invalidateQueries({ queryKey: ["familyPayments"] });
  };

  const handleShowAllPaymentsDueChange = (nextShowAll) => {
    setShowAllPaymentsDue(nextShowAll);
    setCurrentPage(1);
  };

  const selection = useMemo(
    () => ({
      selectedCount: selectedIds.size,
      isSelected: (id) => selectedIds.has(id),
      toggleSelection: (id) =>
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.has(id) ? next.delete(id) : next.add(id);
          return next;
        }),
      selectAll: (rows) => setSelectedIds(new Set(rows.map((r) => r.id))),
      deselectAll: () => setSelectedIds(new Set()),
    }),
    [selectedIds]
  );

  return {
    activeTab,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    searchQuery,
    setSearchQuery,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    showAllPaymentsDue,
    setShowAllPaymentsDue: handleShowAllPaymentsDueChange,
    filteredPayments,
    isLoading,
    totalRecords,
    handleTabChange,
    selection,
  };
};

export { usePaymentsData };
